import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/ru";
import RecordChainMark from "./record-chain-mark";
import { fmtTimeHM } from "../../extensions/utils";
import { getRecordBackground, getRecordLabel, getRecordOccupancy } from "./records-status";
import "./records-week.css";

dayjs.extend(isoWeek);
dayjs.locale("ru");

const HOUR_HEIGHT = 44;
const TIME_GUTTER = 56;

const layoutDayEvents = (events) => {
  const sorted = [...events].sort((a, b) => a.start.valueOf() - b.start.valueOf());
  const clusters = [];
  let current = [];
  let clusterEnd = null;
  sorted.forEach(ev => {
    if (!current.length || ev.start.isBefore(clusterEnd)) {
      current.push(ev);
      if (!clusterEnd || ev.finish.isAfter(clusterEnd)) clusterEnd = ev.finish;
    } else {
      clusters.push(current);
      current = [ev];
      clusterEnd = ev.finish;
    }
  });
  if (current.length) clusters.push(current);

  const result = [];
  clusters.forEach(cluster => {
    const colEnds = [];
    const placed = cluster.map(ev => {
      let col = colEnds.findIndex(end => !ev.start.isBefore(end));
      if (col === -1) {
        col = colEnds.length;
        colEnds.push(ev.finish);
      } else {
        colEnds[col] = ev.finish;
      }
      return { item: ev, col };
    });
    placed.forEach(p => result.push({ ...p, colCount: colEnds.length }));
  });
  return result;
};

/** Недельное расписание: колонки — дни, строки — часы */
const RecordsWeek = ({ date, records, company, onOpenRecord, onOpenDay, onSelectTime }) => {
  const hourStart = company?.hourStart ?? 8;
  const hourFinish = company?.hourFinish ?? 22;
  const hours = Array.from({ length: Math.max(0, hourFinish - hourStart) }, (_, i) => hourStart + i);
  const weekStart = date.startOf("isoWeek");
  const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));
  const totalHeight = hours.length * HOUR_HEIGHT;
  const now = dayjs();
  const nowTop = ((now.hour() * 60 + now.minute()) - hourStart * 60) / 60 * HOUR_HEIGHT;

  const onSlotClick = (day, hour, e) => {
    if (!onSelectTime) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const quarter = Math.min(3, Math.floor((y / HOUR_HEIGHT) * 4));
    const start = day.hour(hour).minute(quarter * 15).second(0).millisecond(0);
    onSelectTime({
      docDate: day,
      start,
      finish: start.add(60, "minute"),
      userId: null,
      roomId: null,
      isGroup: false,
    });
  };

  return (
    <div className="records-week">
      <div className="records-week-head">
        <div style={{ width: TIME_GUTTER }} />
        {days.map(day => (
            <button
              type="button"
              key={day.format("YYYY-MM-DD")}
              className={`records-week-head-cell${day.isoWeekday() >= 6 ? " is-weekend" : ""}${day.isSame(now, "day") ? " is-today" : ""}`}
              onClick={() => onOpenDay(day)}
              title="Открыть день"
            >
              <span className="records-week-dow">{day.format("dd")}</span>
              <span className="records-week-num">{day.date()}</span>
            </button>
        ))}
      </div>
      <div className="records-week-scroll">
        <div className="records-week-hours" style={{ height: totalHeight }}>
          {hours.map(hour => (
            <div key={hour} className="records-week-hour" style={{ height: HOUR_HEIGHT }}>{hour}:00</div>
          ))}
        </div>
        {days.map(day => {
          const isToday = day.isSame(now, "day");
          const dayEvents = layoutDayEvents(
            records.filter(r => r.start && r.finish && r.start.isSame(day, "day"))
          );
          return (
            <div
              key={day.format("YYYY-MM-DD")}
              className={`records-week-day${day.isoWeekday() >= 6 ? " is-weekend" : ""}${isToday ? " is-today" : ""}`}
              style={{ height: totalHeight }}
            >
              {hours.map(hour => (
                <div
                  key={hour}
                  className="records-week-slot"
                  style={{ height: HOUR_HEIGHT }}
                  onClick={e => onSlotClick(day, hour, e)}
                />
              ))}
              {isToday && nowTop >= 0 && nowTop <= totalHeight ? (
                <div className="records-week-now" style={{ top: nowTop }} />
              ) : null}
              {dayEvents.map(({ item, col, colCount }) => {
                const top = (item.start.diff(item.start.startOf("day"), "minute") - hourStart * 60) / 60 * HOUR_HEIGHT;
                const height = Math.max(17, item.finish.diff(item.start, "minute") / 60 * HOUR_HEIGHT - 1);
                const occupancy = getRecordOccupancy(item);
                return (
                  <div
                    key={item.id}
                    className="records-week-event"
                    style={{
                      top,
                      height,
                      left: `calc(${(col / colCount) * 100}% + 2px)`,
                      width: `calc(${100 / colCount}% - 4px)`,
                      backgroundColor: getRecordBackground(item),
                    }}
                    title={`${fmtTimeHM(item.start)}–${fmtTimeHM(item.finish)} ${getRecordLabel(item)}`}
                    onClick={e => {
                      e.stopPropagation();
                      onOpenRecord(item.id);
                    }}
                  >
                    <div className="records-week-event-time">
                      {item.linkId ? <RecordChainMark linkId={item.linkId} /> : null}
                      <span>{fmtTimeHM(item.start)}</span>
                      {occupancy ? <span>{occupancy}</span> : null}
                    </div>
                    {height >= 32 ? (
                      <div className="records-week-event-title">{getRecordLabel(item)}</div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecordsWeek;
